import { useEffect, useState } from 'react';

import type { ComboboxOption } from '../types/ComboboxOption';

interface UseSearchProps {
  /**
   * Current selected option (this needs to stay in options even if it doesn't match the search)
   */
  value?: ComboboxOption[];

  /**
   * Search string
   */
  search?: string;

  /**
   * Callback for search change
   */
  onSearchChange?: (search: string) => void;

  /**
   * Predicate to filter options on the client side
   */
  searchPredicate?: (option: ComboboxOption, search: string) => boolean;

  /**
   * If true, the search will be performed on the client side
   * @default true
   */
  clientSearch?: boolean;

  /**
   * List of options to filter
   */
  options: ComboboxOption[];
}

interface UseSearchReturn {
  /**
   * Current search string
   */
  search: string;

  /**
   * Callback for search change
   */
  setSearch: (search: string) => void;

  /**
   * Filtered options
   */
  options: ComboboxOption[];
}

export const SEARCH_BY_LABEL = (option: { label: string }, search: string) =>
  option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase());

function filterOptions({
  searchPredicate,
  options,
  search,
}: Pick<
  UseSearchProps,
  'searchPredicate' | 'options' | 'search' | 'value'
>): ComboboxOption[] {
  if (!searchPredicate || !search) {
    return options;
  }

  return options.filter((option) => searchPredicate(option, search));
}

export function useSearch({
  search: initialSearch,
  onSearchChange,
  searchPredicate,
  clientSearch,
  options,
  value,
}: UseSearchProps): UseSearchReturn {
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSearch(initialSearch ?? '');
  }, [initialSearch]);

  const searchChangeHandler = (searchText: string) => {
    setSearch(searchText);
    onSearchChange?.(searchText.trim());
  };

  return {
    search,
    setSearch: searchChangeHandler,
    options: clientSearch
      ? filterOptions({ options, search, searchPredicate, value })
      : options,
  };
}
